import type { PlayerProfile } from '@core/state/PlayerProfile';
import { RhythmConfig } from '@modules/rhythm/config';
import { AdventureConfig } from '@modules/adventure/config';
import { TriviaConfig } from '@modules/trivia/config';
import { MemoryConfig } from '@modules/memory/config';

/** Una mision: objetivo a cumplir y recompensa en estrellas al reclamarla. */
export interface MissionDefinition {
  /** Identificador unico (se guarda como reclamada en el perfil). */
  readonly id: string;
  /** Emoji representativo. */
  readonly emoji: string;
  /** Titulo corto. */
  readonly title: string;
  /** Descripcion del objetivo. */
  readonly description: string;
  /** Valor objetivo a alcanzar. */
  readonly target: number;
  /** Estrellas que otorga al reclamarla. */
  readonly reward: number;
  /** Si es true, es una mision diaria (se reinicia cada dia). */
  readonly daily?: boolean;
  /** Progreso actual del jugador hacia el objetivo. */
  readonly progress: (profile: PlayerProfile) => number;
}

/**
 * Catalogo de misiones. El progreso se calcula a partir del perfil (puntajes, partidas,
 * estrellas totales, accesorios), asi que no necesita estado extra mas alla de marcar
 * cuales ya se reclamaron.
 */
export const MISSIONS: readonly MissionDefinition[] = [
  {
    id: 'daily-return',
    emoji: '📅',
    title: 'Diaria: ¡Volviste!',
    description: 'Jugá 1 partida hoy',
    target: 1,
    reward: 10,
    daily: true,
    progress: (profile) => profile.dailyGamesPlayed,
  },
  {
    id: 'daily-three',
    emoji: '🗓️',
    title: 'Diaria: En racha',
    description: 'Jugá 3 partidas hoy',
    target: 3,
    reward: 25,
    daily: true,
    progress: (profile) => profile.dailyGamesPlayed,
  },
  {
    id: 'play1',
    emoji: '🎮',
    title: 'Primeros pasos',
    description: 'Jugá tu primera partida',
    target: 1,
    reward: 8,
    progress: (profile) => profile.gamesPlayed,
  },
  {
    id: 'play10',
    emoji: '🔥',
    title: 'Calentando motores',
    description: 'Jugá 10 partidas',
    target: 10,
    reward: 18,
    progress: (profile) => profile.gamesPlayed,
  },
  {
    id: 'play30',
    emoji: '💪',
    title: 'Imparable',
    description: 'Jugá 30 partidas',
    target: 30,
    reward: 40,
    progress: (profile) => profile.gamesPlayed,
  },
  {
    id: 'rhythm150',
    emoji: '🎵',
    title: 'Ritmo en la sangre',
    description: 'Hacé 150 puntos en Ritmo Pop',
    target: 150,
    reward: 20,
    progress: (profile) => profile.getHighScore(RhythmConfig.key),
  },
  {
    id: 'adventure120',
    emoji: '🏃',
    title: 'Aventurera',
    description: 'Hacé 120 puntos en Aventura',
    target: 120,
    reward: 20,
    progress: (profile) => profile.getHighScore(AdventureConfig.key),
  },
  {
    id: 'trivia60',
    emoji: '🧠',
    title: 'Mente brillante',
    description: 'Hacé 60 puntos en Trivia',
    target: 60,
    reward: 20,
    progress: (profile) => profile.getHighScore(TriviaConfig.key),
  },
  {
    id: 'memory200',
    emoji: '🎴',
    title: 'Buena memoria',
    description: 'Hacé 200 puntos en Memoria',
    target: 200,
    reward: 20,
    progress: (profile) => profile.getHighScore(MemoryConfig.key),
  },
  {
    id: 'shop1',
    emoji: '🛍️',
    title: 'Fashionista',
    description: 'Comprá un accesorio en la tienda',
    target: 1,
    reward: 15,
    progress: (profile) => profile.ownedItems.length,
  },
  {
    id: 'stars300',
    emoji: '🌟',
    title: 'Estrella brillante',
    description: 'Ganá 300 estrellas en total',
    target: 300,
    reward: 60,
    progress: (profile) => profile.totalCoinsEarned,
  },
  {
    id: 'character',
    emoji: '✨',
    title: 'Elegí tu estrella',
    description: 'Elegí tu personaje favorito',
    target: 1,
    reward: 5,
    progress: (profile) => (profile.character !== '' ? 1 : 0),
  },
];
